import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';

// Path prefixes that are part of the dashboard chrome and should respect the
// theme toggle. Everything else (marketing, auth forms, public share/invite/
// sign links, 404, /admin/login) is always light — even for an authenticated
// visitor with dark selected, e.g. opening a share link in a new tab.
const DASHBOARD_PREFIXES = [
  '/files',
  '/folder',
  '/trash',
  '/shares',
  '/shared-with-me',
  '/shared',
  '/messages',
  '/file-requests',
  '/mentions',
  '/signatures',
  '/forms',
  '/notifications',
  '/members',
  '/sessions',
  '/profile',
  '/billing',
];

export function isDashboardRoute(pathname) {
  // The card-before-entry checkout is a standalone, always-light flow.
  if (pathname === '/billing/setup') return false;
  if (pathname.startsWith('/admin')) return pathname !== '/admin/login';
  return DASHBOARD_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Applies `data-theme="dark"` to <html> only on dashboard routes, while
 * authenticated, AND the user has chosen dark (see `index.css`'s
 * `[data-theme='dark']` block). Marketing pages, auth forms, and public
 * share/invite/sign links always render with the light `:root` defaults —
 * independent of both auth state and the dashboard's stored preference.
 */
export function useThemeSync() {
  const isAuthenticated = useAuthStore((s) => Boolean(s.accessToken));
  const theme = useThemeStore((s) => s.theme);
  const { pathname } = useLocation();

  useEffect(() => {
    const root = document.documentElement;
    if (isAuthenticated && theme === 'dark' && isDashboardRoute(pathname)) {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
  }, [isAuthenticated, theme, pathname]);
}
